/**
 * API 文档页数据类型定义
 * 与后端 /points/api-source-doc/current 接口返回格式完全一致
 */

// 请求方法枚举
export type ApiMethod = "GET" | "POST" | "WS" | "INFO";

// 请求方法样式分类（P=POST 黑色，G=GET 绿色，W=WS 棕色）
export type MethodClass = "P" | "G" | "W";

// 代码示例支持的编程语言（curl = cURL/Bash 调用示例）
export type SampleLang = "python" | "java" | "curl";

// 代码示例
export interface ApiSample {
  python?: string;
  java?: string;
  curl?: string;
  response?: string;
}

// 请求参数（对象数组形式，与后端一致）
export interface ApiParam {
  name: string;
  type: string;
  required: string;
  default: string;
  desc: string;
}

// 响应字段（对象数组形式，与后端一致）
export interface ApiResponseField {
  field: string;
  type: string;
  desc: string;
}

// 单条接口定义
export interface ApiItem {
  id: string;
  title: string;
  method: ApiMethod;
  path: string;
  desc: string;
  notice?: string | null;
  noteChips?: string[] | null;
  params: ApiParam[];
  responses: ApiResponseField[];
  sample: ApiSample | null;
  placeholder?: boolean;
  // 协议型接口的补充说明小节（接口地址/认证方式/连接步骤/协议说明/Event 定义/Proto 文件等）
  sections?: ApiSection[] | null;
}

// sections 表格行：字段名统一为 col1、col2、col3...
export type ApiSectionTableRow = Record<
  string,
  string | number | null | undefined
>;

// sections 表格：兼容两种后端返回形式
// 1) 直接是行数组（每项含 col1/col2/col3...）
// 2) 对象形式 { headers?: string[], rows: ApiSectionTableRow[] }
export type ApiSectionTable =
  | ApiSectionTableRow[]
  | {
      headers?: string[] | null;
      rows: ApiSectionTableRow[];
    };

// 接口补充说明小节
export interface ApiSection {
  title: string;
  content?: string | null;
  table?: ApiSectionTable | null;
}

// 接口分组
export interface ApiGroup {
  id: string;
  title: string;
  emoji: string | null;
  icon: string;
  items: ApiItem[];
}

// 错误码定义
export interface ApiErrorCode {
  code: string;
  info: string;
  desc: string;
}

// 计费规则
export interface ApiBillingRule {
  module: string;
  rule: string;
}

// 文档元信息
export interface ApiDocMeta {
  errorCodes: ApiErrorCode[];
  billing: ApiBillingRule[];
}

// 完整 API 文档数据（接口返回的 data 字段）
export interface ApiDocData {
  baseUrl: string;
  groups: ApiGroup[];
  meta: ApiDocMeta;
}

// 根据请求方法返回样式分类
export const getMethodClass = (method: ApiMethod): MethodClass => {
  if (method === "GET") return "G";
  if (method === "WS") return "W";
  return "P";
};

// 分组图标兜底映射（后端 emoji 为 null 时使用）
// 按中文菜单名 / 英文 icon key 双重匹配
export const getGroupEmoji = (
  emoji: string | null,
  icon: string,
  title?: string,
): string => {
  if (emoji) return emoji;
  const iconMap: Record<string, string> = {
    // —— 按中文菜单名匹配（后端可能把中文标题当 icon）——
    // 风格统一：暗色/中性实体物件 emoji，避免亮色与卡通感，与 📇 🎬 协调
    "API 指引": "📓",

    视频创作: "🎬",
    音频处理: "📻",
    语音克隆: "🎙️",
    语音合成: "📇",
    音色设计: "🎛️",
    翻译: "🌐",
    字幕擦除: "🧹",
    asr识别: "🎙️",
    asr: "🎙️",
    "tts websocket": "📡",
    "tts-websocket": "📡",
    公共说话人分类: "👥",
    背景音与人声分离: "🎚️",
    个人音色管理: "🗄️",
    视频压制合成: "🎞️",
    公共音色列表查询: "📇",
    错误码: "🚫",
    计费说明: "💳",
    任务管理和音色查询: "🎚️",
    // —— 按英文 icon key 匹配（后端配 icon 字段时）——
    "mic-2": "🎙️", // 语音识别
    mic: "🎙️",
    "audio-lines": "📇", // 语音合成
    audio: "📇",
    "volume-2": "🎚️",
    music: "🎵",
    languages: "🌐", // 翻译
    translate: "🌐",
    globe: "🌐",
    video: "🎬", // 视频
    film: "🎞️",
    "file-text": "📄",
    file: "📄",
    "book-open": "📑",
    book: "📑",
    image: "🖼️",
    palette: "🎛️", // 音色设计
    brain: "🧠",
    sparkles: "✨",
    bot: "🤖",
    send: "📤",
    radio: "📡", // WebSocket 流式
    wifi: "📡",
    lock: "🔐",
    shield: "🛡️",
    key: "🔑",
    database: "🗄️",
    "hard-drive": "💾",
    info: "ℹ️",
    "alert-triangle": "🚫",
    "circle-dollar-sign": "💳",
    tag: "🏷️",
    puzzle: "🧩",
    settings: "⚙️",
  };
  // icon 可能是中文也可能是英文 key，统一小写匹配一次（中文不受影响）
  const key = icon ? icon.trim() : "";
  // 优先用 icon 匹配，匹配不到时再用 title 匹配
  if (iconMap[key] || iconMap[key.toLowerCase()]) {
    return iconMap[key] || iconMap[key.toLowerCase()];
  }
  // icon 未命中时，用分组标题兜底匹配
  if (title) {
    const titleKey = title.trim();
    if (iconMap[titleKey] || iconMap[titleKey.toLowerCase()]) {
      return iconMap[titleKey] || iconMap[titleKey.toLowerCase()];
    }
  }
  return "📇";
};

/**
 * 接口概览卡片专用：简化描述映射
 * 按接口标题精确匹配，返回一句话简明描述；未命中时回退到原始 desc
 * 仅用于接口概览区的卡片展示，不影响接口详情页等其他位置
 */
const cardDescMap: Record<string, string> = {
  // —— 语音 & 音色 ——
  语音克隆: "上传人声音频，生成专属音色模型",
  语音合成: "文本转语音，支持 16 种语言与两种合成模式",
  // 实时语音合成（WebSocket）：兼容后端返回的 WS / WebSocket 两种标题变体
  "实时语音合成(WS)": "双向流式文本转语音，支持 16 种语言，低延迟实时输出",
  "实时语音合成（WebSocket）":
    "双向流式文本转语音，支持 16 种语言，低延迟实时输出",
  "实时语音合成(GRPC)": "基于 gRPC 双向流的实时语音合成",
  "实时语音合成（GRPC）": "基于 gRPC 双向流的实时语音合成",
  音色设计: "用自然语言描述生成指定特征音色",
  音色列表查询: "查询平台公共音色列表",
  // —— 视频创作 ——
  字幕翻译: "提交字幕，翻译为目标语言后下载",
  查询任务状态: "轮询翻译任务的进度与状态",
  下载翻译结果: "任务完成后下载 srt 结果文件",
  获取任务列表: "查询历史翻译任务列表",
  字幕擦除: "擦除视频中的字幕内容",
  查询字幕擦除结果: "根据 project_id 查询擦除结果",
  视频压制合成: "音频合成与字幕压制异步处理",
  查询视频压制合成任务: "查询压制任务的进度与结果",
  // —— 音频处理 ——
  语音识别: "语音转文字、说话人识别与人声分离",
  // 语音识别（WS）：兼容后端返回的 WS / WebSocket 两种标题变体
  "语音识别(WS)": "实时流式语音识别，建议单次 ≤15 秒",
  "语音识别（WebSocket）": "实时流式语音识别，建议单次 ≤15 秒",
  "ASR WebSocket API": "实时流式语音识别，建议单次 ≤15 秒",
  "ASR WS API": "实时流式语音识别，建议单次 ≤15 秒",
  说话人识别: "上传音频，提交说话人分类异步任务",
  查询说话人识别任务: "轮询说话人分类任务状态",
  背景音与人声分离: "上传音频与字幕，提交分离任务",
  查询背景音与人声分离任务: "查询分离任务进度与结果",
  //任务管理和音色查询
  个人音色查询: "查询个人音色信息",
  个人音色删除: "删除个人音色",
  统一任务查询: "用于统一查询异步任务的处理进度和结果",
};

/**
 * 标题归一化：去首尾空格 + 中英文括号统一为英文括号，便于精确匹配
 */
const normalizeTitle = (title: string): string => {
  return (title || "").trim().replace(/（/g, "(").replace(/）/g, ")");
};

/**
 * 获取接口概览卡片的简化描述
 * 命中映射返回简化描述，否则回退到原始 desc
 */
export const getCardDesc = (title: string, fallback: string): string => {
  const key = normalizeTitle(title);
  // 同步对映射表的 key 做归一化匹配
  for (const mapKey of Object.keys(cardDescMap)) {
    if (normalizeTitle(mapKey) === key) {
      return cardDescMap[mapKey];
    }
  }
  return fallback;
};

// ===== 音色试听：表格展示行结构 =====
// 数据来源：GET /video/public-voice-tone/page 接口实时查询（不再使用硬编码数据）
export interface VoicePreviewRow {
  name: string; // 音色名称
  language: string; // 语言
  desc: string; // 描述
  demoAudioUrl: string; // 试听音频地址
}
